import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid, TableCell, TableRow, Typography } from '@material-ui/core';
import { fade, makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import { CheckedMode } from 'types/app';

interface IProps {
  noun: string;
  count: number;
  total: number;
  mode: CheckedMode;
  onSelectAll(mode: CheckedMode): void;
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    height: 0,
    backgroundColor: fade(theme.palette.primary.main, 0.1),
    transition: theme.transitions.create('height'),
    overflow: 'hidden',
  },
  visibleRow: {
    height: 48,
  },
  cell: {
    flex: 1,
    padding: 0,
    border: 0,
  },
  visibleCell: {
    padding: 6,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  button: {
    fontWeight: theme.typography.fontWeightBold,
    padding: 0,
  },
}));

const RowSelection: FC<IProps> = ({ noun, count, total, mode, onSelectAll }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isVisible = mode !== CheckedMode.OFF;

  const tableRowClasses = classNames(classes.root, {
    [classes.visibleRow]: isVisible,
  });

  const tableCellClasses = classNames(classes.cell, {
    [classes.visibleCell]: isVisible,
  });

  function renderPageSelected() {
    return (
      <Grid container justify="center" alignItems="center">
        <Grid item>
          <Typography variant="body2">
            {t('components:rowSelectionSelected', { count, noun })}
          </Typography>
        </Grid>
        <Grid item>
          <Button color="primary" onClick={() => onSelectAll(CheckedMode.ALL)}>
            {t('components:rowSelectionSelectAll', { count: total, noun })}
          </Button>
        </Grid>
      </Grid>
    );
  }

  function renderAllSelected() {
    return (
      <Grid container justify="center" alignItems="center">
        <Grid item>
          <Typography variant="body2">
            {t('components:rowSelectionAllSelected', { count: total, noun })}
          </Typography>
        </Grid>
        <Grid item>
          <Button color="primary" onClick={() => onSelectAll(CheckedMode.OFF)}>
            {t('components:rowSelectionClear')}
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <TableRow className={tableRowClasses}>
      <TableCell className={tableCellClasses}>
        {mode === CheckedMode.ALL && renderAllSelected()}
        {mode === CheckedMode.PAGE && renderPageSelected()}
      </TableCell>
    </TableRow>
  );
};

export default RowSelection;
